import React, { useState, useEffect } from 'react';
import styles from './component.module.css';
import { FaPlus } from 'react-icons/fa6';
import { FiMinus } from 'react-icons/fi';
import { BreathAPIs } from '../../APIs/BreathAPIs';

const PRICE_ENTRY_REGEX = /[\d.]+\s*kWh\s*@\s*\$[\d.]+\s*\/\s*kWh\s*\([^)]*\)/g;
const SESSION_LABEL_REGEX = /S\d+/;

function parsePriceBreakdownEntries(priceText) {
  if (!priceText) {
    return [];
  }
  const matches = priceText.match(PRICE_ENTRY_REGEX);
  const rawEntries = matches && matches.length > 0 ? matches : [priceText.trim()];
  return rawEntries
    .map((rawEntry) => {
      const trimmedLine = rawEntry.trim();
      const atIndex = trimmedLine.indexOf('@');
      if (atIndex === -1) {
        return { left: trimmedLine, time: '', right: '' };
      }
      const energyPart = trimmedLine.slice(0, atIndex).trim();
      let afterAt = trimmedLine.slice(atIndex + 1).trim();
      let parenPart = '';
      const parenIndex = afterAt.indexOf('(');
      if (parenIndex !== -1 && afterAt.endsWith(')')) {
        parenPart = afterAt.slice(parenIndex).trim();
        afterAt = afterAt.slice(0, parenIndex).trim();
      }
      const energyMatch = energyPart.match(/([\d.]+)\s*kWh/i);
      const rateMatch = afterAt.match(/\$([\d.]+)\s*\/\s*kWh/i);
      const total = (energyMatch && rateMatch) ? parseFloat(energyMatch[1]) * parseFloat(rateMatch[1]) : null;
      return { left: energyPart, time: parenPart, right: afterAt, total };
    })
    .filter((entry) => entry.left || entry.right);
}

function parseSessionPriceBreakdown(priceText) {
  if (!priceText) {
    return [];
  }
  const parts = priceText.split(/(S\d+)/).map((part) => part.trim()).filter(Boolean);

  const hasSessionLabels = parts.some((part) => SESSION_LABEL_REGEX.test(part) && part.length <= 4);
  if (!hasSessionLabels) {
    return [{ label: null, entries: parsePriceBreakdownEntries(priceText) }];
  }

  const groups = [];
  let current = null;
  parts.forEach((part) => {
    if (/^S\d+$/.test(part)) {
      current = { label: part, entries: [] };
      groups.push(current);
    } else if (current) {
      current.entries.push(...parsePriceBreakdownEntries(part));
    } else {
      groups.push({ label: null, entries: parsePriceBreakdownEntries(part) });
    }
  });

  return groups.filter((group) => group.entries.length > 0);
}

function getDateValue(value) {
  if (!value) {
    return null;
  }
  return value.$date ? value.$date : value;
}

function formatOutageDuration(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

function formatDurationHMS(totalMinutes) {
  const totalSeconds = Math.max(0, Math.round((totalMinutes || 0) * 60));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function getCurrentType(chargerLevel) {
  const level = (chargerLevel || '').toUpperCase();
  if (level === 'L2') return 'AC';
  if (level === 'L3') return 'DC';
  return '';
}

const BillingBlock = ({ bookingId, unUtilizedFee, idleDuration, unUtilizedTimeSec, nonReservable, estimateAmount=0, timeFormatAmPm}) => {
  const [ariaExpand, setAriaExpand] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [totalCost, setTotalCost] = useState(0.0)
  const [taxCost, setTaxCost] = useState(0)
  const [powerOffExpanded, setPowerOffExpanded] = useState(false);
  // console.log("nonReservable: ", nonReservable)

  useEffect(() => {
    const fetchBilling = async () => {
      try {
        const data = await BreathAPIs.getBilling(bookingId);
        setBillingData(data.data);
        // console.log("Unutilized fee billing block:",unUtilizedFee)
        // console.log("Unutilized time billing block:",unUtilizedTimeSec)
        // console.log(billingData?.taxCost)
        setTaxCost(billingData?.taxCost ? billingData?.taxCost : 0)
        setTotalCost(billingData?.totalCost)
      } catch (error) {
        console.error('Error fetching billing data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // if (ariaExpand) {
    //   fetchBilling();
    // }
    fetchBilling();
  }, [ariaExpand, bookingId]);

  function convertSecondsToHoursAndMinutes(totalSeconds) {
      const hours = Math.floor(totalSeconds / 3600);
      const remainingSeconds = totalSeconds % 3600;
      const minutes = Math.floor(remainingSeconds / 60);
      return `${hours} h ${minutes} m`;
  }

  return (
    <div className={styles['billing-block']}>
      <button onClick={() => setAriaExpand(!ariaExpand)} className={styles['expand-button']}>
        {ariaExpand ? <FiMinus /> : <FaPlus />}
        ${estimateAmount === 0 ? '0.00' : (billingData?.grandTotal ? (parseFloat(billingData.grandTotal.toFixed(2)) + ((billingData?.booking?.nightChargeEstimate !== undefined  && billingData?.booking?.nightChargeEstimate !== null && billingData?.booking?.nightChargeEstimate > 0) ? parseFloat(billingData?.booking?.nightChargeEstimate.toFixed(2)) : 0)).toFixed(2) : '0.00')}
        
      </button>

      {(ariaExpand && isLoading) && (
        <p className={`card-text placeholder-wave ${styles['loading']}`}>
          <span className="placeholder col-12"></span>
        </p>
      )}
      {/* {console.log("billing data : ", billingData?.utilizedEnergy)} */}
      {ariaExpand && !isLoading && (
        <div className={styles['data']}>
          <div className={styles['data-row']}>Utilized Energy <span>{billingData?.utilizedEnergy ? billingData?.utilizedEnergy.toFixed(4) : '0.0000'} kWh</span></div>
          {(billingData?.booking?.maxPower !== undefined && billingData?.booking?.maxPower !== null) && (() => {
            const currentType = getCurrentType(billingData?.booking?.charger?.chargerLevel);
            return (
              <div className={styles['data-row']}>Max Power  <span>{billingData?.booking.maxPower ? billingData?.booking.maxPower.toFixed(2) : '0.00'} kW{currentType && ` (${currentType})`}</span></div>
            );
          })()}
          <div className={styles['data-row']}>Utilized Time <span>{billingData?.utilizedTime?.total ? convertSecondsToHoursAndMinutes(billingData?.utilizedTime?.total) : '0 h'}</span></div>
          {/* <div className={styles['data-row']}>Total Paid Consumed Energy <span>{billingData?.totalPaidConsumedEnergy ? billingData?.totalPaidConsumedEnergy : 0} kWh</span></div>
          <div className={styles['data-row']}>Total Free Consumed Energy <span>{billingData?.totalFreeConsumedEnergy ? billingData?.totalFreeConsumedEnergy : 0} kWh</span></div> */}
          {/* <div className={styles['data-row']}>Tax <span>${'0.00'}</span></div> */}
          {!nonReservable && <div className={styles['data-row']}>Unutilized Time <span>{unUtilizedTimeSec ? ((unUtilizedTimeSec - 10*60) > 0 ? convertSecondsToHoursAndMinutes(unUtilizedTimeSec - 10*60) : '0 h') : '0 h'}</span></div> }
          { billingData?.booking?.bookingType !== 'paid' ?
            <div className={styles['data-row']}>Idle Fee <span>$ 0.00</span></div>
            :
            <div className={styles['data-row']}>Idle Time <span>{formatDurationHMS(idleDuration)}</span></div>
            }
          { estimateAmount !==0 && (
            <div className={styles['data-row-group']}>
              <div className={styles['data-row']}>Energy Cost<span>${(totalCost - unUtilizedFee - taxCost).toFixed(2)}</span></div>
              
            </div>
          )}
          {billingData?.booking?.isNightCharging && <div className={styles['data-row']}>Overnight Fee <span>${billingData?.booking?.nightChargeEstimate >= 0 ? billingData?.booking?.nightChargeEstimate?.toFixed(2) : "0.00"}</span></div>}
          {!nonReservable && (estimateAmount !==0) && <div className={styles['data-row']}>Unutilized Time Fee <span>${unUtilizedFee.toFixed(2)}</span></div>}
          { estimateAmount !==0 && <div className={styles['data-row']}>Idle Fee ({formatDurationHMS(idleDuration)} @ ${(billingData?.booking?.charger?.location?.idleFee || 0).toFixed(2)}/min)<span>${billingData?.IdleCost ? billingData?.IdleCost.toFixed(2) : '0.00'}</span></div>}
          {/* { estimateAmount !==0 && idleDuration > 0 && billingData?.IdleCost > 0 && (
            <div className={styles['data-row']}>Idle Cost Breakdown <span>{formatDurationHMS(idleDuration)} @ ${(billingData?.booking?.charger?.location?.idleFee || 0).toFixed(2)}/min &nbsp;&nbsp; ${billingData.IdleCost.toFixed(2)}</span></div>
          )} */}
          {estimateAmount !==0 && <div className={styles['data-row']}>Tax({billingData?.taxPercent ? billingData.taxPercent : '0'}%) <span>${billingData?.taxCost ? billingData.taxCost.toFixed(2) : '0.00'}</span></div>}
          {
            billingData?.booking?.priceText && (
              <div className={`${styles['data-row']} ${styles['price-breakdown-row']}`}>
                <div className={styles['price-breakdown-title']}>Energy Rate Breakdown</div>
                {parseSessionPriceBreakdown(billingData.booking.priceText).map((group, gIdx) => (
                  <div key={gIdx} className={styles['price-breakdown-session']}>
                    {group.label && <div className={styles['price-breakdown-session-label']}>{group.label}</div>}
                    {group.entries.map((entry, idx) => (
                      <div key={idx} className={styles['price-breakdown-line']}>
                        <span>
                          {entry.left}
                          {entry.time && <span className={styles['price-breakdown-time']}> {entry.time}</span>}
                          {entry.right && `@${entry.right}`}
                        </span>
                        {entry.total !== null && entry.total !== undefined && <span>${entry.total.toFixed(2)}</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )
          }

          {
            billingData?.booking?.powerOffPeriod && billingData.booking.powerOffPeriod.length > 0 && (
              <div className={`${styles['data-row']} ${styles['power-off-row']}`}>
                <button
                  type="button"
                  onClick={() => setPowerOffExpanded(!powerOffExpanded)}
                  className={styles['power-off-toggle']}
                >
                  {powerOffExpanded ? <FiMinus /> : <FaPlus />} Power Outages ({billingData.booking.powerOffPeriod.length})
                </button>
                {powerOffExpanded && billingData.booking.powerOffPeriod.map((period, idx) => {
                  const offlineDate = new Date(getDateValue(period.offlineTime));
                  const bootDate = new Date(getDateValue(period.bootTime));
                  const durationSeconds = Math.max(0, Math.round((bootDate.getTime() - offlineDate.getTime()) / 1000));
                  const timeOpts = { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: timeFormatAmPm };
                  return (
                    <div key={period._id?.$oid || period._id || idx} className={styles['power-off-line']}>
                      <span>{offlineDate.toLocaleTimeString('en-US', timeOpts)} - {bootDate.toLocaleTimeString('en-US', timeOpts)}</span>
                      <span>{formatOutageDuration(durationSeconds)}</span>
                    </div>
                  );
                })}
              </div>
            )
          }

          {estimateAmount !==0 && (
            <div className={`${styles['data-row-group']} ${styles['data-row-group-big']}`}>
              <div className={`${styles['data-row']} ${styles['big']}`}>Total Paid <span>${(billingData?.grandTotal ? (parseFloat(billingData.grandTotal.toFixed(2)) + ((billingData?.booking?.nightChargeEstimate !== undefined  && billingData?.booking?.nightChargeEstimate !== null && billingData?.booking?.nightChargeEstimate > 0) ? parseFloat(billingData?.booking?.nightChargeEstimate.toFixed(2)) : 0)).toFixed(2) : '0.00')}</span></div>
              {(billingData?.booking?.charger?.location?.showRateSplitInfo && billingData?.revenueModel?.revenueType === 'rev_share') && (
                <ul className={`${styles['rate-split-list']} ${styles['rate-split-list-big']}`}>
                  <li><span>Property Energy Cost</span><span>${((totalCost - unUtilizedFee - taxCost) * billingData?.revenueModel?.RSF).toFixed(2)}</span></li>
                  <li><span>BreatheEV Fee</span><span>${((totalCost + (billingData?.IdleCost ? billingData?.IdleCost : 0)) - ((totalCost - unUtilizedFee - taxCost) * billingData?.revenueModel?.RSF)).toFixed(2)}</span></li>
                </ul>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default BillingBlock;
